import React, { useState } from 'react';
import { Button, HStack, Text, VStack } from '@adc/parallax-component-library';
import * as Yup from 'yup';
import { Dialog } from 'tamagui';
import i18n from '../../utilities/i18n';
import './Modal.scss';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import TextInputWithError from '../ui/forms/TextInputWithError';
import MessageBanner from '../messageBanner/MessageBanner';
import { Organization } from '../../models';
import log from '../../utilities/log';
import { organizationNameSchema } from 'adc-sirius-module-common';

type Props = {
  trigger: React.ReactNode;
  onSuccess: (organization: Organization) => Promise<void>;
};

type FormValues = {
  name: string;
  type: string;
  region: string;
  description: string;
};

export function CreateOrganizationModal({ trigger, onSuccess }: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const initialValues: FormValues = {
    name: '',
    type: '',
    region: '',
    description: '',
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .required(i18n.t('Form.genericRequired'))
      .matches(
        organizationNameSchema.matcher,
        i18n.t('Form.allowedSpecialCharacters', {
          characters: organizationNameSchema.allowedSpecialCharacters.join(' '),
        }),
      )
      .test(
        'len',
        () =>
          i18n.t('Form.genericMaxLength', {
            count: organizationNameSchema.maxLength,
          }),
        (val) =>
          organizationNameSchema.maxLength && val
            ? val.length < organizationNameSchema.maxLength
            : true,
      ),
    type: Yup.string().optional(),
    region: Yup.string().optional(),
    description: Yup.string().optional(),
  });

  const onSubmit = async (values: any, helpers: FormikHelpers<any>) => {
    const requestData = {
      ...values,
    };

    try {
      await onSuccess(requestData);
      helpers.resetForm();
      setIsOpen(false);
      setErrorMessage('');
    } catch (err) {
      log.error(err);

      const error = err as { message?: string };

      setErrorMessage(
        error.message || 'Modal_CreateOrganization.error_generic',
      );
    }
  };

  return (
    <Dialog open={isOpen}>
      <Dialog.Trigger asChild onPress={() => setIsOpen(true)}>
        {trigger}
      </Dialog.Trigger>

      <Dialog.Portal>
        <Dialog.Overlay key="overlay" />
        <Dialog.Content
          key="content"
          width="50%"
          maxHeight="90%"
          display="flex"
          flexDirection="column"
        >
          <HStack justifyContent="space-between">
            <Dialog.Title>
              <Text type="$h3.strong">
                {i18n.t('Modal_CreateOrganization.title')}
              </Text>
            </Dialog.Title>
          </HStack>

          {errorMessage ? (
            <VStack paddingVertical={'$4'}>
              <MessageBanner
                bodyText={i18n.t(errorMessage)}
                type="danger"
                testID="create-organization-error-banner"
              />
            </VStack>
          ) : null}

          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
          >
            {(formikProps: FormikProps<FormValues>) => (
              <Form
                id="create-organization-form"
                noValidate
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                  flex: 1,
                }}
              >
                <VStack
                  gap="$2"
                  paddingVertical="$2"
                  overflow="scroll"
                  flex={1}
                >
                  <TextInputWithError
                    label={i18n.t('Entity.name')}
                    aria-label={i18n.t('Entity.name')}
                    name="name"
                    testID="name"
                    disabled={formikProps.isSubmitting}
                  />
                  <TextInputWithError
                    label={i18n.t('Entity.type')}
                    aria-label={i18n.t('Entity.type')}
                    name="type"
                    testID="type"
                    disabled={formikProps.isSubmitting}
                  />
                  <TextInputWithError
                    label={i18n.t('Entity.region')}
                    aria-label={i18n.t('Entity.region')}
                    name="region"
                    testID="region"
                    disabled={formikProps.isSubmitting}
                  />
                  <TextInputWithError
                    label={i18n.t('Entity.description')}
                    aria-label={i18n.t('Entity.description')}
                    name="description"
                    testID="description"
                    disabled={formikProps.isSubmitting}
                  />
                </VStack>
                <HStack
                  justifyContent="flex-end"
                  gap="$2"
                  paddingTop="$4"
                  flexShrink={0}
                >
                  <Button
                    variant="secondary"
                    label={i18n.t('Common.cancel')}
                    aria-label={i18n.t('Common.cancel')}
                    testID="cancel"
                    disabled={formikProps.isSubmitting}
                    onPress={() => {
                      setIsOpen(false);
                      setErrorMessage('');
                      formikProps.resetForm();
                    }}
                  />
                  <Button
                    variant="primary"
                    onPress={formikProps.submitForm}
                    label={i18n.t('Common.save')}
                    testID="save-organization"
                    disabled={formikProps.isSubmitting || !formikProps.isValid}
                  />
                </HStack>
              </Form>
            )}
          </Formik>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  );
}
